import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cover, posts } from "../../data/blogData";
import "../../styles/blog-home.css";

const Icon = {
  calendar: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="17" height="15" rx="3" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  ),
  clock: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  arrow: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  left: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  ),
};

const BlogHome = () => {
  const viewportRef = useRef(null);
  const [state, setState] = useState({
    prev: false,
    next: true,
    left: 0,
    width: 33,
  });

  const update = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setState({
      prev: el.scrollLeft > 4,
      next: el.scrollLeft < max - 4,
      left: (el.scrollLeft / el.scrollWidth) * 100,
      width: (el.clientWidth / el.scrollWidth) * 100,
    });
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update]);

  const scrollByCard = (dir) => {
    const el = viewportRef.current;
    const cards = el.querySelectorAll(".gt9-card");
    const step =
      cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="gt9-blog" aria-labelledby="gt9-blog-title">
      {/* shared gradients for the illustrated covers */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="gt9-bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0b1a3a" />
            <stop offset="1" stopColor="#14264f" />
          </linearGradient>
          <radialGradient id="gt9-glow" cx="0.82" cy="0.1" r="0.7">
            <stop offset="0" stopColor="#d2a566" stopOpacity=".38" />
            <stop offset="1" stopColor="#d2a566" stopOpacity="0" />
          </radialGradient>
          <pattern
            id="gt9-grid"
            width="28"
            height="28"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M28 0H0V28"
              fill="none"
              stroke="#fff"
              strokeOpacity=".05"
            />
          </pattern>
        </defs>
      </svg>

      <div className="gt9-container">
        <div className="gt9-blog__head">
          <div>
            <span className="gt9-blog__eyebrow">Blog &amp; news</span>
            <h2 id="gt9-blog-title" className="gt9-blog__title">
              Insights to keep your business{" "}
              <span className="hl">one step ahead</span>
            </h2>
            <p className="gt9-blog__desc">
              Practical guides on cloud, security and IT strategy, written by
              the people who run it every day.
            </p>
          </div>
          <div className="gt9-blog__nav">
            <button
              type="button"
              className="gt9-blog__arrow"
              aria-label="Previous articles"
              disabled={!state.prev}
              onClick={() => scrollByCard(-1)}
            >
              {Icon.left}
            </button>
            <button
              type="button"
              className="gt9-blog__arrow"
              aria-label="Next articles"
              disabled={!state.next}
              onClick={() => scrollByCard(1)}
            >
              {Icon.arrow}
            </button>
          </div>
        </div>

        <div
          className="gt9-blog__viewport"
          ref={viewportRef}
          onScroll={update}
          tabIndex={0}
          role="region"
          aria-label="Latest articles"
        >
          <div className="gt9-blog__track">
            {posts.map((p) => (
              <article className="gt9-card" key={p.slug}>
                <Link
                  className="gt9-card__media"
                  to={`/blogs/${p.slug}`}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  {p.image ? (
                    <img src={p.image} alt="" loading="lazy" />
                  ) : (
                    <Cover type={p.cover} />
                  )}
                  <span className="gt9-card__tag">{p.tag}</span>
                </Link>
                <div className="gt9-card__body">
                  <div className="gt9-card__meta">
                    <span>
                      {Icon.calendar}
                      <time dateTime={p.iso}>{p.date}</time>
                    </span>
                    <span>
                      {Icon.clock}
                      {p.read} min read
                    </span>
                  </div>
                  <h3 className="gt9-card__title">
                    <Link to={`/blogs/${p.slug}`}>{p.title}</Link>
                  </h3>
                  <p className="gt9-card__text">{p.text}</p>
                  <Link className="gt9-card__more" to={`/blogs/${p.slug}`}>
                    Read more {Icon.arrow}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="gt9-blog__foot">
          <div className="gt9-blog__progress" aria-hidden="true">
            <span
              style={{ width: `${state.width}%`, left: `${state.left}%` }}
            />
          </div>
          <Link to="/blogs" className="gt9-blog__btn">
            View all articles {Icon.arrow}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogHome;
